import { Alert, Snackbar } from '@mui/material';
import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

type SnackbarContextType = {
  showSnackbar: (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const handleClose = () => {
    setOpen(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setMessage(message);
    setSeverity(severity);
    setOpen(true);
  };

  const value = useMemo(() => ({ showSnackbar }), []);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={3500}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleClose}
          severity={severity}
          variant="filled"
          className="w-full"
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (context === undefined) {
      throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
  };